import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SiteLayout } from "@/components/site/Layout";
import { useSeo } from "@/context/ContentContext";
import { ClinicCard } from "@/components/site/ClinicCard";
import { api } from "@/lib/api";

export default function Clinics() {
  useSeo("clinics");
  const [params, setParams] = useSearchParams();
  const discipline = params.get("discipline") || "all";
  const city = params.get("city") || "all";
  const [q, setQ] = useState(params.get("q") || "");
  const [disciplines, setDisciplines] = useState([]);
  const [cities, setCities] = useState([]);
  const [clinics, setClinics] = useState(null);

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  useEffect(() => {
    const p = {};
    if (discipline !== "all") p.discipline = discipline;
    if (city !== "all") p.city = city;
    if (params.get("q")) p.q = params.get("q");
    api.get("/clinics", { params: p }).then((r) => setClinics(r.data));
  }, [discipline, city, params]);

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (!val || val === "all") next.delete(key); else next.set(key, val);
    setParams(next);
  };

  const title = disciplines.find((d) => d.slug === discipline)?.name || "All clinics";

  return (
    <SiteLayout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="eyebrow">Browse</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-2" data-testid="clinics-title">{title}</h1>
        <form className="mt-8 flex flex-wrap gap-3" onSubmit={(e) => { e.preventDefault(); update("q", q); }}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clinic name" className="pl-10 rounded-full h-11 bg-card" data-testid="nav-search-input" />
          </div>
          <Select value={discipline} onValueChange={(v) => update("discipline", v)}>
            <SelectTrigger className="w-52 rounded-full h-11 bg-card" data-testid="hero-search-discipline-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All treatments</SelectItem>
              {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={(v) => update("city", v)}>
            <SelectTrigger className="w-44 rounded-full h-11 bg-card" data-testid="hero-search-location-input"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <button type="submit" className="rounded-full h-11 px-6 bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors" data-testid="hero-search-submit-button">Search</button>
        </form>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="clinics-grid">
          {clinics?.map((c) => <ClinicCard key={c.id} c={c} />)}
        </div>
        {clinics && clinics.length === 0 && <p className="mt-10 text-muted-foreground" data-testid="clinics-empty">No clinics found. Try another treatment or city.</p>}
      </section>
    </SiteLayout>
  );
}
